import ChartMetric from '@/Enums/Chart/Metric.enum';
import GraphPaginationService from '@/Services/Graphs/GraphPagination.service';
import ChartLogos from '@/components/Charts/ChartLogos';
import TablePagination from '@/components/Table/TablePagination';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, ArcElement, Legend, ChartDataLabels);

interface CustomChartOptions extends ChartOptions<'bar'> {
  maxBarThickness?: number;
  borderRadius?: number;
  borderSkipped?: boolean;
}

interface IProps {
  chartData: ChartData<'bar'>;
  logos?: string[];
  rowsPerPageOptions?: number[];
  paginate?: boolean;
  legendLabel: ChartMetric;
}

const VerticalChart = ({ chartData, logos, rowsPerPageOptions = [10, 25, 50], paginate, legendLabel }: IProps) => {
  //#region pagination
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);

  const { datasets, labels } = useMemo(() => chartData, [chartData]);

  const total = useMemo(() => labels?.length || 0, [labels]);
  const totalPages = useMemo(() => Math.ceil(total / rowsPerPage), [total, rowsPerPage]);

  const currentPage = useMemo(() => (page === -1 ? totalPages : page), [page, totalPages]);
  const { start, end } = useMemo(
    () =>
      paginate
        ? GraphPaginationService.findStartToEnd(currentPage, totalPages, total, rowsPerPage)
        : { start: 0, end: total },
    [currentPage, totalPages, total, rowsPerPage, paginate],
  );
  const allDataValues = useMemo(() => datasets?.[0]?.data || [], [datasets]);
  const suggestedMax = useMemo(() => Math.ceil(Math.max(...(allDataValues as number[]))), [allDataValues]);

  const processedLogos = useMemo(() => logos?.slice(start, end) || [], [logos, start, end]);
  const processedLabels = useMemo(() => labels?.slice(start, end) || [], [labels, start, end]);
  const processedData = useMemo(() => datasets[0]?.data?.slice(start, end) || [], [datasets, start, end]);

  const handleChangePage = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, newPage: number) => {
      setPage?.(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
    },
    [setRowsPerPage],
  );

  const displayedStart = total ? start + 1 : 0;
  const displayedEnd = Math.min(end, total);
  //#endregion

  if (!processedLabels?.length) return null;

  const displayedData: ChartData<'bar'> = useMemo(
    () => ({
      labels: processedLabels,
      datasets: [{ ...datasets, label: legendLabel, data: processedData }],
    }),
    [processedLabels, processedData, datasets],
  );

  const options: CustomChartOptions = useMemo(
    () => ({
      interaction: {
        intersect: false,
      },
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          align: 'start',
          labels: {
            boxHeight: 12,
            boxWidth: 12,
            padding: 0,
          },
        },
      },
      maxBarThickness: 47,
      borderRadius: 5,
      borderSkipped: false,
      backgroundColor: '#193B2C',
      scales: {
        y: {
          suggestedMax,
          grid: {
            drawTicks: false,
          },
          border: {
            display: false,
            dash: [6, 3],
          },
        },
        x: {
          ticks: {
            autoSkip: false,
            color: '#84818A',
            font: {
              family: 'Plus Jakarta Sans',
              size: 12,
              weight: 'normal',
              lineHeight: 3,
            },
          },
        },
      },
      maintainAspectRatio: false,
    }),
    [suggestedMax],
  );

  return (
    <div className='flex flex-col w-full'>
      <div className='bar_chart'>
        <Bar style={{ height: 350, width: '90%' }} options={options} data={displayedData} />
      </div>
      {logos && rowsPerPage === 10 && <ChartLogos logos={processedLogos} scale={suggestedMax} />}
      {paginate ? (
        <TablePagination
          sx={{ padding: 0, marginTop: '8px' }}
          page={currentPage}
          totalPages={totalPages}
          start={displayedStart}
          end={displayedEnd}
          total={total}
          rowsPerPageOptions={rowsPerPageOptions}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      ) : null}
    </div>
  );
};

export default VerticalChart;
